import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";

const assets = [
  { file: "/logo.svg",                 label: "Primary icon (SVG, scalable)",     size: "1.7 KB",  recommend: "Use everywhere — website, documents, print." },
  { file: "/logo-1024.png",            label: "Primary icon (PNG 1024×1024)",     size: "95 KB",   recommend: "Social profile pictures (Facebook, LinkedIn)." },
  { file: "/logo-512.png",             label: "Primary icon (PNG 512×512)",       size: "40 KB",   recommend: "Gmail profile picture — upload this one." },
  { file: "/logo-256.png",             label: "Primary icon (PNG 256×256)",       size: "17 KB",   recommend: "Email signatures, small in-app badges." },
  { file: "/logo-wordmark.svg",        label: "Horizontal wordmark (SVG)",        size: "1.2 KB",  recommend: "Email headers, PDF letterheads." },
  { file: "/logo-wordmark-1200.png",   label: "Horizontal wordmark (PNG 1200×300)", size: "31 KB",  recommend: "Website headers, email banners, handbooks." },
];

export default function BrandAssets() {
  const copyLink = (file) => {
    const url = window.location.origin + file;
    navigator.clipboard.writeText(url);
    toast.success("Public URL copied.");
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="brand-back">
          <ArrowLeft className="w-4 h-4" /> Back to ChildCheckOh
        </Link>

        <header className="mt-6">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Brand kit</div>
          <h1 className="mt-2 font-display text-5xl font-medium text-stone-900 tracking-tight">Download the logo.</h1>
          <p className="mt-3 text-stone-600 max-w-2xl">
            Drop the right file into Gmail, your website footer, parent handbooks, or any other place you'd like to show the ChildCheckOh mark. SVG is crispest; PNG is universally supported.
          </p>
        </header>

        {/* Big preview cards */}
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <div className="rounded-3xl bg-white border border-stone-200 p-10 grid place-items-center shadow-sm">
            <img src="/logo.svg" alt="ChildCheckOh icon" className="w-56 h-56" data-testid="brand-icon-preview" />
          </div>
          <div className="rounded-3xl bg-stone-100 p-10 grid place-items-center shadow-sm">
            <img src="/logo-wordmark.svg" alt="ChildCheckOh wordmark" className="w-full max-w-md" data-testid="brand-wordmark-preview" />
          </div>
        </div>

        {/* Gmail-specific helper */}
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="gmail-tip">
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-800">Set as your Gmail profile picture</div>
          <ol className="mt-2 text-sm text-emerald-900 space-y-1 list-decimal list-inside">
            <li>Right-click <b>Primary icon (PNG 512×512)</b> below and save the file.</li>
            <li>Open <a className="underline font-semibold" href="https://myaccount.google.com/personal-info" target="_blank" rel="noreferrer">myaccount.google.com/personal-info</a> → click your current photo → <b>Add profile picture</b> → upload the file you just saved.</li>
            <li>Google may take 10–30 minutes to show the new image across Gmail, Google Chat, and Workspace.</li>
          </ol>
        </div>

        {/* Asset list */}
        <div className="mt-8 rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="grid grid-cols-1 divide-y divide-stone-100">
            {assets.map((a) => (
              <div key={a.file} className="flex items-center gap-4 p-4 hover:bg-stone-50" data-testid={`brand-asset-${a.file.replace(/[^a-z0-9]/gi, "-")}`}>
                <div className="w-12 h-12 rounded-xl bg-stone-100 grid place-items-center shrink-0 overflow-hidden">
                  <img src={a.file} alt="" className="max-w-full max-h-full" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-stone-900">{a.label}</div>
                  <div className="text-[11px] text-stone-500 truncate">{a.recommend} · {a.size}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => copyLink(a.file)} className="hidden sm:inline-flex">
                  <Copy className="w-4 h-4 mr-1" /> Copy URL
                </Button>
                <Button asChild size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
                  <a href={a.file} download>
                    <Download className="w-4 h-4 mr-1" /> Download
                  </a>
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 text-xs text-stone-500 space-y-1">
          <div><b className="text-stone-700">Primary palette:</b> emerald-700 <code className="bg-stone-100 px-1 rounded">#047857</code> · stone-950 <code className="bg-stone-100 px-1 rounded">#0c0a09</code> · accent orange-400 <code className="bg-stone-100 px-1 rounded">#fb923c</code>.</div>
          <div><b className="text-stone-700">Typography:</b> Headings — Fraunces (Display). Body — Inter.</div>
        </div>
      </div>
    </div>
  );
}
